// src/main.js
// Application entry point. Wires the audio engine, track manager, and UI.

import * as Tone from 'tone';
import { engine } from './audio/engine.js';
import { TrackManager } from './tracks/TrackManager.js';
import { parseMidiFile, exportMidi, downloadMidi } from './tracks/midiLoader.js';
import { InstrumentPicker } from './ui/InstrumentPicker.js';
import { Transport } from './ui/Transport.js';
import { Mixer } from './ui/Mixer.js';
import { NoteEditor } from './ui/NoteEditor.js';
import { PianoKeyboard } from './ui/PianoKeyboard.js';
import { Sequencer } from './sequencer/Sequencer.js';
import { ClipEditor } from './ui/ClipEditor.js';
import { ClipLibraryPanel } from './ui/ClipLibraryPanel.js';
import { PRESETS } from './data/presets.js';
import { t, onLocaleChange, getLocale } from './i18n.js';
import { SampleStorage } from './project/SampleStorage.js';
import { ProjectManager } from './project/ProjectManager.js';
import { FileMenu } from './ui/FileMenu.js';
import { CustomInstrumentRegistry } from './instruments/CustomInstrumentRegistry.js';
import { CustomInstrumentDialog } from './ui/CustomInstrumentDialog.js';
import { ThemeSwitcher, initTheme } from './ui/ThemeSwitcher.js';
import { setCookie, getCookie } from './cookie.js';
import { clipLibrary } from './clips/ClipLibrary.js';

// Apply saved theme immediately (before any UI renders)
initTheme();

// Expose a single shared TrackManager for cross-module access (e.g. Transport)
export const trackManager = new TrackManager();

// Global singleton instances for custom instruments and sample storage
export const customRegistry = new CustomInstrumentRegistry();
export const sampleStorage = new SampleStorage();

// Hoisted so loadPreset / MIDI-import can call sequencer.setSelectedTrack()
// after addTrack to keep the piano keyboard activated.
let sequencer;

const els = {
  welcomeModal: document.getElementById('welcome-modal'),
  workspace: document.getElementById('workspace'),
  startButton: document.getElementById('start-button'),
  instrumentPicker: document.getElementById('instrument-picker'),
  presetButtons: document.getElementById('preset-buttons'),
  transport: document.getElementById('transport'),
  mixer: document.getElementById('mixer'),
  sequencer: document.getElementById('sequencer'),
  noteEditor: document.getElementById('note-editor'),
  pianoKeyboard: document.getElementById('piano-keyboard'),
  clipLibraryPanel: document.getElementById('clip-library-panel')
};

// ---------------- boot ----------------

// Boot UI immediately — workspace is always visible now.
initUI();

// Welcome modal: show unless user dismissed it.
if (!getCookie('helix.welcomeDismissed')) {
  els.welcomeModal.classList.remove('hidden');
}

els.startButton.addEventListener('click', async () => {
  // Initialise IndexedDB-backed sample storage for custom instruments
  await sampleStorage.open().catch(err => console.warn('SampleStorage init:', err));
  await engine.start();
  // Hide welcome modal
  els.welcomeModal.classList.add('hidden');
  // Remember preference
  if (document.getElementById('welcome-dismiss')?.checked) {
    setCookie('helix.welcomeDismissed', '1');
  }
});

function initUI() {
  // Theme switcher in the header
  new ThemeSwitcher(document.getElementById('theme-switcher'));

  // Instrument picker
  new InstrumentPicker(els.instrumentPicker, {
    onSelect: async (entry) => {
      const track = await trackManager.addTrack({ instrumentId: entry.id });
      // Give the new track a little example note so the user sees something
      track.addNote({ time: 0, duration: 1, pitch: 60, velocity: 0.8 });
      // Auto-select the new track so the piano keyboard activates
      // immediately, without requiring a second click on the lane tab.
      sequencer.setSelectedTrack(track.id);
    },
    customRegistry,
    onAddCustom: () => {
      const dialog = new CustomInstrumentDialog(sampleStorage, customRegistry, async (createdEntry) => {
        const track = await trackManager.addTrack({ instrumentId: createdEntry.id });
        track.addNote({ time: 0, duration: 1, pitch: 60, velocity: 0.8 });
        sequencer.setSelectedTrack(track.id);
      });
      dialog.open();
    }
  });

  // Wire sample storage into TrackManager for custom instrument audio loading
  trackManager.setSampleStorage(sampleStorage);

  // Project system — must be created after all UI components are wired
  const projectManager = new ProjectManager(trackManager, clipLibrary, sampleStorage, customRegistry, engine);
  new FileMenu(document.getElementById('file-menu'), projectManager);

  // Preset buttons
  els.presetButtons.innerHTML = '';
  for (const preset of PRESETS) {
    const btn = document.createElement('button');
    btn.className = 'preset-btn';
    const pName = getLocale() === 'zh' ? (preset.nameZh || preset.name) : preset.name;
      const pDesc = getLocale() === 'zh' ? (preset.descriptionZh || preset.description) : preset.description;
      btn.innerHTML = `<strong>${pName}</strong><span>${pDesc}</span>`;
    btn.addEventListener('click', () => loadPreset(preset));
    els.presetButtons.appendChild(btn);
  }

  // Sequencer (created first so other components can reference it).
  sequencer = new Sequencer(els.sequencer, trackManager);

  // Transport — needs the sequencer ref to check the selected track
  // when the user clicks Record.
  new Transport(els.transport, trackManager, sequencer);

  // Mixer
  new Mixer(els.mixer, trackManager);

  // Note editor (per-track note list)
  new NoteEditor(els.noteEditor, trackManager, sequencer);

  // Piano keyboard (computer-keyboard + on-screen) for audition + record
  new PianoKeyboard(els.pianoKeyboard, { trackManager, sequencer });

  // Clip editor (mini piano-roll for editing clip definitions)
  const clipEditor = new ClipEditor(els.noteEditor);
  sequencer.onClipEdit((clipId) => {
    clipEditor.open(clipId);
  });

  // Clip library panel (drag clips onto the timeline)
  new ClipLibraryPanel(els.clipLibraryPanel, trackManager);

  // MIDI import/export + clear
  setupMidiIo();

  // Keyboard shortcuts
onLocaleChange(() => {
    // Rebuild toolbar so labels update on locale change
    const existing = els.sequencer.parentNode?.querySelector('.io-toolbar');
    if (existing) { existing.remove(); setupMidiIo(); }
  });
onLocaleChange(() => {
    // Rebuild preset buttons so labels update on locale change
    els.presetButtons.innerHTML = '';
    for (const preset of PRESETS) {
      const btn = document.createElement('button');
      btn.className = 'preset-btn';
      const pName = getLocale() === 'zh' ? (preset.nameZh || preset.name) : preset.name;
      const pDesc = getLocale() === 'zh' ? (preset.descriptionZh || preset.description) : preset.description;
      btn.innerHTML = `<strong>${pName}</strong><span>${pDesc}</span>`;
      btn.addEventListener('click', () => loadPreset(preset));
      els.presetButtons.appendChild(btn);
    }
  });
  window.addEventListener('keydown', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    if (e.code === 'Space') {
      e.preventDefault();
      document.getElementById('btn-play')?.click();
    }
  });
}

// ---------------- preset loading ----------------

async function loadPreset(preset) {
  trackManager.clearAll();
  if (preset.bpm) engine.setBpm(preset.bpm);
  for (const t of preset.tracks) {
    const track = await trackManager.addTrack({
      instrumentId: t.instrumentId,
      name: t.name,
      mixer: t.mixer || {}
    });
    track.setNotes(t.notes);
  }
  // Auto-select the first track so the piano is active right away.
  if (trackManager.tracks.length > 0) {
    sequencer.setSelectedTrack(trackManager.tracks[0].id);
  }
}

// ---------------- MIDI I/O ----------------

function setupMidiIo() {
  // Add a small toolbar above the sequencer for MIDI import / export.
  // Project save/load lives in the File menu.
  const toolbar = document.createElement('div');
  toolbar.className = 'io-toolbar';
  toolbar.innerHTML = `
    <label class="io-btn">
      <input type="file" id="midi-import" accept=".mid,.midi" hidden />
      <span>${t('midi.import')}</span>
    </label>
    <button class="io-btn" id="midi-export">${t('midi.export')}</button>
  `;
  els.sequencer.parentNode.insertBefore(toolbar, els.sequencer);

  toolbar.querySelector('#midi-import').addEventListener('change', async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const buf = await file.arrayBuffer();
    const tracks = parseMidiFile(buf);
    trackManager.clearAll();
    for (let i = 0; i < tracks.length; i++) {
      const t = tracks[i];
      // Cycle through catalog instruments for variety
      const instrumentId = pickInstrumentForIndex(i);
      const track = await trackManager.addTrack({
        instrumentId,
        name: t.name || t('midi.trackFallback', i + 1)
      });
      track.setNotes(t.notes);
    }
    if (trackManager.tracks.length > 0) {
      sequencer.setSelectedTrack(trackManager.tracks[0].id);
    }
    e.target.value = '';
  });

  toolbar.querySelector('#midi-export').addEventListener('click', () => {
    const bytes = exportMidi(trackManager.tracks, Tone.Transport.bpm.value);
    downloadMidi(bytes, 'helix-arrangement.mid');
  });
}

const IMPORT_ROTATION = [
  'piano', 'violin', 'flute', 'cello', 'harp', 'clarinet', 'guitar', 'guzheng', 'pipa'
];
function pickInstrumentForIndex(i) {
  return IMPORT_ROTATION[i % IMPORT_ROTATION.length];
}
